import { useCallback, useMemo } from 'react';
import { useResourceList } from '../../../engine/api/hooks';
import { useEngine } from '../../../engine/api/context';
import { useStatuses, useTechnicians, type StatusRow } from './catalog';

// The working set behind the ticket workspace's queue rail (docs/servicedesk-v2-proposal.md P1):
// the ordered list of tickets the technician is working through, so `j`/`k` and "send and next"
// never leave the queue. One list query, shared by the rail and the navigation helpers; rows are
// narrowed to the open status categories client-side because "open" is a property of the
// tenant's status catalog, not a column.

export type WorkingSetMode = 'mine' | 'queue' | 'all';

export const WORKING_SET_MODES: { key: WorkingSetMode; label: string; hint: string }[] = [
  { key: 'mine', label: 'Mine', hint: 'Assigned to me and still open' },
  { key: 'queue', label: 'Queue', hint: "Everything open in this ticket's queue" },
  { key: 'all', label: 'All', hint: 'Every open ticket, oldest first' },
];

export const WORKING_SET_STORAGE_KEY = 'hf.sd.workspace.mode';

/** Status categories that still need work; the rail hides everything else. */
const OPEN_CATEGORIES: ReadonlySet<StatusRow['category']> = new Set(['new', 'open', 'pending'] as const);

export interface WorkingSetRow {
  id: string;
  ref: string | null;
  summary: string | null;
  status_id: string | null;
  priority_id: string | null;
  queue_id: string | null;
  assigned_tech_id: string | null;
  created_at: string;
  due_at: string | null;
}

/** The technician row for the signed-in user (assignment is by technician, not user). */
export function useMyTechnicianId(): string | null {
  const { principal } = useEngine();
  const techs = useTechnicians();
  return useMemo(() => {
    if (!principal.id) return null;
    return techs.rows.find((t) => t.user_id === principal.id)?.id ?? null;
  }, [techs.rows, principal.id]);
}

export interface WorkingSet {
  mode: WorkingSetMode;
  rows: WorkingSetRow[];
  isLoading: boolean;
  /** Position of a ticket in the set, or -1. */
  indexOf: (id: string) => number;
  /** The ticket after / before `id`; null at the ends (or when `id` is not in the set). */
  nextId: (id: string) => string | null;
  prevId: (id: string) => string | null;
  refetch: () => void;
}

const RAIL_LIMIT = 100;

/**
 * Load the rail's tickets. `mine` needs the technician id (null until the catalog loads, which
 * simply yields an empty set); `queue` needs the current ticket's queue.
 */
export function useWorkingSet(mode: WorkingSetMode, opts: { queueId?: string | null } = {}): WorkingSet {
  const myTechId = useMyTechnicianId();
  const statuses = useStatuses();
  const queueId = opts.queueId ?? null;

  const filter = useMemo(() => {
    const f: { field: string; op: 'eq' | 'is_null'; value: string | boolean }[] = [{ field: 'merged_into_ticket_id', op: 'is_null', value: true }];
    if (mode === 'mine' && myTechId) f.push({ field: 'assigned_tech_id', op: 'eq', value: myTechId });
    if (mode === 'queue' && queueId) f.push({ field: 'queue_id', op: 'eq', value: queueId });
    return f;
  }, [mode, myTechId, queueId]);

  // `mine` with no technician row, and `queue` with no queue, have nothing to ask for.
  const enabled = mode === 'all' || (mode === 'mine' ? Boolean(myTechId) : Boolean(queueId));
  const list = useResourceList<WorkingSetRow>('tickets', { filter, sort: 'created_at' }, { limit: RAIL_LIMIT, enabled, keepPrevious: true });

  const rows = useMemo(() => {
    if (statuses.rows.length === 0) return list.rows;
    const open = new Set(statuses.rows.filter((s) => OPEN_CATEGORIES.has(s.category)).map((s) => s.id));
    return list.rows.filter((r) => !r.status_id || open.has(r.status_id));
  }, [list.rows, statuses.rows]);

  const indexOf = useCallback((id: string) => rows.findIndex((r) => r.id === id), [rows]);
  const nextId = useCallback(
    (id: string) => {
      const i = rows.findIndex((r) => r.id === id);
      return i >= 0 && i + 1 < rows.length ? (rows[i + 1]?.id ?? null) : null;
    },
    [rows],
  );
  const prevId = useCallback(
    (id: string) => {
      const i = rows.findIndex((r) => r.id === id);
      return i > 0 ? (rows[i - 1]?.id ?? null) : null;
    },
    [rows],
  );

  return { mode, rows, isLoading: enabled && list.isLoading, indexOf, nextId, prevId, refetch: list.refetch };
}
